// Simple icon components instead of lucide-react
const TrendingUpIcon = () => (
  <svg className="h-3 w-3 text-green-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline>
    <polyline points="17 6 23 6 23 12"></polyline>
  </svg>
);

const TrendingDownIcon = () => (
  <svg className="h-3 w-3 text-red-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 18 13.5 8.5 8.5 13.5 1 6"></polyline>
    <polyline points="17 18 23 18 23 12"></polyline>
  </svg>
);

interface KpiCardProps {
  title: string | React.ReactNode;
  value: string;
  delta?: {
    value: string;
    type: "positive" | "negative" | "neutral";
    label?: string;
  };
  tooltip?: string;
  icon: React.ReactNode;
}

export function KpiCard({ title, value, delta, icon, tooltip }: KpiCardProps) {
  const getDeltaStyles = (type: "positive" | "negative" | "neutral") => {
    switch (type) {
      case "positive":
        return "bg-green-50 text-green-600 border-green-200";
      case "negative":
        return "bg-red-50 text-red-600 border-red-200";
      default:
        return "bg-gray-100 text-gray-600 border-gray-200";
    }
  };

  const getDeltaIcon = (type: "positive" | "negative" | "neutral") => {
    switch (type) {
      case "positive":
        return <TrendingUpIcon />;
      case "negative":
        return <TrendingDownIcon />;
      default:
        return null;
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-premium hover:shadow-premium-hover transition-all duration-200 hover:-translate-y-0.5">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-4">
            {/* Icon - now comes with its own container */}
            {icon}
            
            {/* Content */}
            <div className="flex-1">
              <div className="text-sm font-medium text-gray-600 mb-1 flex items-center gap-2">
                <span>{typeof title === 'string' ? title : title}</span>
                {tooltip && (
                  <span
                    title={tooltip}
                    role="img"
                    aria-label={tooltip}
                    className="ml-1 text-gray-400 hover:text-gray-600 cursor-help"
                  >
                    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <path d="M12 16v.01" />
                      <path d="M12 12a2 2 0 1 0-2-2" />
                    </svg>
                  </span>
                )}
              </div>
              <p className="text-2xl font-semibold text-gray-900 tracking-tight" style={{ fontFeatureSettings: '"tnum"' }}>
                {value}
              </p>
            </div>
          </div>

          {/* Delta */}
          {delta && (
            <div className="mt-4">
              <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${getDeltaStyles(delta.type)}`}>
                {getDeltaIcon(delta.type)}
                <span>{delta.value}</span>
                 {delta.label && <span className="text-gray-500">{delta.label}</span>}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}